import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { GAME_STATUS, ACTIONS, getCanvasConfig } from './utils/constants.js';
import {
  initializeCanvas,
  render,
  clearCanvas,
  drawVerticalLines,
  drawPlayerLabels,
  drawRewardCards,
  drawShufflingRewardCards,
  getCanvasCoords,
  findPlayerIndexAtPoint,
} from './utils/ladderRenderer.js';
import { loadLadderAssets } from './utils/ladderImages.js';
import { LadderAnimator } from './utils/ladderAnimator.js';
import LadderControls from './LadderControls.jsx';
import './LadderCanvas.css';

const gameInstruction = {
  ko: {
    title: '참여 방법',
    step1: '원하는 캐릭터를 선택해 주세요.',
    step2: 'START 버튼을 누르면 게임이 시작됩니다.',
    step3: '축구공이 골대에 도착하면 포인트 획득!',
  },
  en: {
    title: 'How to Play',
    step1: 'Select your desired character.',
    step2: 'Press the START button to begin the game.',
    step3: 'Earn points when the soccer ball reaches the goal!',
  },
};

function renderSelectingState(ctx, state, config, language, hoveredIndex, assets, gameRewards) {
  clearCanvas(ctx, config, assets);
  if (state.ladderData.verticalLines.length === 0) return;

  drawVerticalLines(ctx, state.ladderData.verticalLines, config);
  drawPlayerLabels(ctx, { ...config, language }, state.ladderData.verticalLines, config, {
    interactive: true,
    selectedStartIndex: state.selectedStartIndex,
    hoveredIndex,
    assets,
  });
  drawRewardCards(ctx, state.ladderData.verticalLines, config, language, {
    rewards: gameRewards,
    showText: false,
    assets,
  });
}

/**
 * LadderCanvas - Canvas rendering component for ladder game
 */
export default function LadderCanvas({
  state,
  dispatch,
  config,
  language = 'ko',
  assets = null,
  gameRewards = null,
  hasToken = false,
  canPlay = true,
  isLoadingStatus = false,
  isStartingGame = false,
  loginToPlay,
  campaignName,
  onLogin,
  onStartGame,
  bg,
}) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const animatorRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [imagesReady, setImagesReady] = useState(0);

  const canvasConfig = getCanvasConfig(config);
  const isSelecting = state.status === GAME_STATUS.SELECTING;

  const assetKey = useMemo(() => JSON.stringify(assets), [assets]);

  useEffect(() => {
    if (!assets) {
      setImagesReady((n) => n + 1);
      return;
    }

    let cancelled = false;
    loadLadderAssets(assets)
      .then(() => {
        if (!cancelled) setImagesReady((n) => n + 1);
      })
      .catch(() => {
        if (!cancelled) setImagesReady((n) => n + 1);
      });

    return () => {
      cancelled = true;
    };
  }, [assetKey, assets]);

  const handleCanvasClick = useCallback(
    (event) => {
      if (!hasToken || !canPlay || isLoadingStatus || isStartingGame) return;
      if (state.status !== GAME_STATUS.SELECTING) return;
      if (!canvasRef.current || state.ladderData.verticalLines.length === 0) return;

      const { x, y } = getCanvasCoords(canvasRef.current, event.clientX, event.clientY, config);
      const index = findPlayerIndexAtPoint(x, y, state.ladderData.verticalLines, config, assets);

      if (index !== null) {
        dispatch({
          type: ACTIONS.SELECT_START,
          payload: { startIndex: index },
        });
      }
    },
    [
      hasToken,
      canPlay,
      isLoadingStatus,
      isStartingGame,
      state.status,
      state.ladderData.verticalLines,
      config,
      dispatch,
      assets,
    ],
  );

  const handleCanvasMouseMove = useCallback(
    (event) => {
      if (!hasToken || !canPlay || isLoadingStatus || isStartingGame) return;
      if (state.status !== GAME_STATUS.SELECTING) return;
      if (!canvasRef.current || state.ladderData.verticalLines.length === 0) return;

      const { x, y } = getCanvasCoords(canvasRef.current, event.clientX, event.clientY, config);
      const index = findPlayerIndexAtPoint(x, y, state.ladderData.verticalLines, config, assets);
      setHoveredIndex(index);
    },
    [hasToken, canPlay, isLoadingStatus, isStartingGame, state.status, state.ladderData.verticalLines, config, assets],
  );

  const handleCanvasMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;

    const ctx = initializeCanvas(canvasRef.current, config);
    ctxRef.current = ctx;
    render(ctx, state, config, language, assets);
  }, [imagesReady]);

  useEffect(() => {
    if (!isSelecting) {
      setHoveredIndex(null);
    }
  }, [isSelecting]);

  useEffect(() => {
    if (!ctxRef.current) return;

    if (state.status === GAME_STATUS.SELECTING) {
      renderSelectingState(ctxRef.current, state, config, language, hoveredIndex, assets, gameRewards);
    } else if (state.status === GAME_STATUS.SHUFFLING) {
      return;
    } else {
      render(ctxRef.current, state, config, language, assets);
    }
  }, [state, config, language, hoveredIndex, assets, gameRewards, imagesReady]);

  useEffect(() => {
    if (state.status !== GAME_STATUS.SHUFFLING) return;
    if (!ctxRef.current || !state.shuffledRewards) return;

    let animationFrameId;
    const startTime = Date.now();
    const duration = 1200;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      clearCanvas(ctxRef.current, config, assets);
      if (state.ladderData.verticalLines.length > 0) {
        drawVerticalLines(ctxRef.current, state.ladderData.verticalLines, config);
        drawPlayerLabels(ctxRef.current, { ...config, language }, state.ladderData.verticalLines, config, { assets });
        drawShufflingRewardCards(
          ctxRef.current,
          state.ladderData.verticalLines,
          config,
          language,
          state.rewards,
          state.shuffledRewards,
          progress,
          assets,
        );
      }

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animate();

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [state.status, state.shuffledRewards, state.ladderData.verticalLines, config, language, assets, imagesReady]);

  useEffect(() => {
    if (state.status !== GAME_STATUS.ANIMATING) return;
    if (!state.ladderData.path || state.ladderData.path.length === 0) return;

    const animator = new LadderAnimator(state.ladderData.path);
    animatorRef.current = animator;

    animator.start(
      (position, progress) => {
        dispatch({
          type: ACTIONS.UPDATE_ANIMATION,
          payload: { progress },
        });

        if (canvasRef.current && position) {
          const canvas = canvasRef.current;
          const canvasRect = canvas.getBoundingClientRect();
          const canvasConfig = getCanvasConfig(config);
          const scaleFactor = canvasRect.height / canvasConfig.height;
          const ballScreenY = canvasRect.top + position.y * scaleFactor;
          const viewportCenter = window.innerHeight / 2;
          const scrollOffset = ballScreenY - viewportCenter;

          if (Math.abs(scrollOffset) > 10) {
            window.scrollTo({
              top: window.scrollY + scrollOffset,
              behavior: 'smooth',
            });
          }
        }
      },
      () => {
        dispatch({
          type: ACTIONS.COMPLETE_ANIMATION,
        });
      },
    );

    return () => {
      if (animatorRef.current) {
        animatorRef.current.stop();
      }
    };
  }, [state.status, state.ladderData.path, state.selectedStartIndex, state.ladderData.verticalLines, dispatch, config]);

  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current && ctxRef.current) {
        const ctx = initializeCanvas(canvasRef.current, config);
        ctxRef.current = ctx;

        if (state.status === GAME_STATUS.SELECTING) {
          renderSelectingState(ctx, state, config, language, hoveredIndex, assets, gameRewards);
        } else if (state.status !== GAME_STATUS.SHUFFLING) {
          render(ctx, state, config, language, assets);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [state, config, language, hoveredIndex, assets, gameRewards, imagesReady]);

  return (
    <div className="ladder-canvas">
      <LadderControls
        state={state}
        language={language}
        hasToken={hasToken}
        canPlay={canPlay}
        isLoadingStatus={isLoadingStatus}
        isStartingGame={isStartingGame}
        loginToPlay={loginToPlay}
        campaignName={campaignName}
        onLogin={onLogin}
        onStartGame={onStartGame}
      />

      <div
        className="ladder-canvas__container"
        style={{
          backgroundImage: bg ? `url(${bg})` : 'none',
        }}
      >

        <div className="ladder-canvas__instruction-container mt-auto">
          <div className="flex flex-col w-full">
            <p className="bg-[#FFCBCBD4] text-center w-fit px-2 font-semibold mb-2 lg:mb-6 mx-auto sm:text-xl lg:text-3xl">
              {gameInstruction[language].title}
            </p>

            <div className="flex flex-col gap-y-3 font-medium text-xs sm:text-xl lg:text-2xl">
              <div className='flex gap-x-2'>
                <p className="bg-[#FFCBCBD4] px-2 font-semibold max-w-[60px] sm:max-w-[90px] lg:max-w-[120px] w-full h-fit">STEP 1.</p>
                <p>{gameInstruction[language].step1}</p>
              </div>

              <div className='flex gap-x-2'>
                <p className="bg-[#FFCBCBD4] px-2 font-semibold max-w-[60px] sm:max-w-[90px] lg:max-w-[120px] w-full h-fit">STEP 2.</p>
                <p>{gameInstruction[language].step2}</p>
              </div>

              <div className='flex gap-x-2'>
                <p className="bg-[#FFCBCBD4] px-2 font-semibold max-w-[60px] sm:max-w-[90px] lg:max-w-[120px] w-full h-fit">STEP 3.</p>
                <p>{gameInstruction[language].step3}</p>
              </div>
            </div>
          </div>
        </div>

        <canvas
          ref={canvasRef}
          className={`ladder-canvas__element${isSelecting ? ' ladder-canvas__element--selecting' : ''}`}
          style={{
            aspectRatio: `${canvasConfig.width} / ${canvasConfig.height}`,
            marginTop: 'auto',
          }}
          onClick={handleCanvasClick}
          onMouseMove={handleCanvasMouseMove}
          onMouseLeave={handleCanvasMouseLeave}
          role="img"
          aria-label={language === 'ko' ? '사다리 타기 게임' : 'Ladder game'}
        />
      </div>
    </div>
  );
}